import React from 'react';
import _ from 'lodash';
import { useSlidesGrid } from 'components/OrderSlidesList/hooks/useSlidesGrid';
import { useSlidesListData } from 'components/OrderSlidesList/hooks/useSlidesListData';
import { MiniDrawerWithContextMemo } from 'components/Layout/drawer';
import {
  ANNOTATION_UX_V2_FIELD,
  SLIDES_FOR_TEMP_GROUP,
  TEMP_GROUP_TITLE,
  THUMBNAIL_SWITCH_FIELD,
} from 'components/ImageViewer/constants';
import { RenderAdjustableGridOrderSlideList } from 'components/OrderSlidesList/OrderSlidesListViewAdjustableGrid';
import { currentUserPreferencesSelector } from 'store/slices/userDetailsSlice';
import { useSelector } from 'react-redux';
import { useSlidesListViewV2Styles } from 'components/OrderSlidesList/OrderSlidesListViewV2Styles';
import {
  getFromLS,
  getSlideViewerPageTitle,
} from 'components/OrderSlidesList/utilities';
import { useTitle } from 'components/utilities/hooks/useTitle';
import { ResizeableThumbnail } from 'components/ThumbnailQRCode/ResizeableThumbnail';
import Container from '@mui/material/Container';
import { ShortcutsPrompt } from 'components/OrderSlidesList/components/ShortcutsPrompt';
import {
  getSlidesListLeadingCrumb,
  SLIDE_VIEWER_COMPACT_LAYOUT_VARS,
  SlideViewerAppBarActions,
  SlideViewerAppBarTitle,
} from 'components/ImageViewer/components/SlideViewerAppBar/SlideViewerAppBar';

export const OrderSlidesListView2 = () => {
  const preferences = useSelector(currentUserPreferencesSelector);
  const slidesForTempGroup = getFromLS(SLIDES_FOR_TEMP_GROUP);
  const isSlidesForTempGroupNotEmpty =
    slidesForTempGroup && !_.isEmpty(slidesForTempGroup);
  const {
    handlePreviousSlide,
    handleNextSlide,
    selectedRows,
    annotationsCount,
    setAnnotationsCount,
    slide,
    slidesViewerProps,
    slidesGroupProps,
    slidesTableProps,
    slideCommentsProps,
  } = useSlidesGrid();
  const { projectUUID, slidesGroupName, slideGroupUUID } = useSlidesListData({
    slidesForTempGroup,
    isSlidesForTempGroupNotEmpty,
  });
  const { classes } = useSlidesListViewV2Styles();
  const pageTitle = getSlideViewerPageTitle(slide);
  useTitle(pageTitle);

  // The contextual annotation UX folds the breadcrumb and the viewer controls
  // into the app bar, like the single slide page, so the slide gets the rows.
  const isCompactHeader = !!preferences[ANNOTATION_UX_V2_FIELD];

  const drawerOrderHeader = slide
    ? `Order ${slide.order?.id} Slides List`
    : 'Order Slides List';

  const drawerProjectHeader = slide
    ? `${slide.project?.name} Slides List`
    : 'Project Slides List';

  const drawerHeader = projectUUID ? drawerProjectHeader : drawerOrderHeader;
  // The legacy title puts a group's name in the italic suffix; the compact
  // title (shown until a slide is selected) folds it into the text.
  let header = drawerHeader;
  let plainHeader = drawerHeader;

  if (isSlidesForTempGroupNotEmpty) {
    header = TEMP_GROUP_TITLE;
    plainHeader = TEMP_GROUP_TITLE;
  } else if (slidesGroupName) {
    header = 'Group: ';
    plainHeader = `Group: ${slidesGroupName}`;
  }

  const compactHeader = (
    <SlideViewerAppBarTitle
      slide={slide}
      leading={getSlidesListLeadingCrumb({
        isTempGroup: isSlidesForTempGroupNotEmpty,
        slidesGroupName,
        projectUUID,
        slide,
      })}
      withSlidesCrumb={false}
      fallback={plainHeader}
    />
  );
  const isMultipleSelected = selectedRows.length > 1;

  return (
    <>
      <MiniDrawerWithContextMemo
        header={isCompactHeader ? compactHeader : header}
        headerItalic={isCompactHeader ? undefined : slidesGroupName}
        headerActions={
          isCompactHeader && slide ? <SlideViewerAppBarActions /> : null
        }
        compactContent={isCompactHeader}
        withoutBackground
      >
        <div
          className={classes.root}
          style={isCompactHeader ? SLIDE_VIEWER_COMPACT_LAYOUT_VARS : undefined}
        >
          <Container maxWidth={false} disableGutters>
            <RenderAdjustableGridOrderSlideList
              showFavoriteIcon={selectedRows.length === 1}
              isSlidesGroupViewer={!!slideGroupUUID}
              isHeaderInAppBar={isCompactHeader}
              annotationsCount={annotationsCount}
              slidesViewerProps={slidesViewerProps}
              slidesGroupProps={slidesGroupProps}
              slidesTableProps={slidesTableProps}
              slideCommentsProps={slideCommentsProps}
              setAnnotationsCount={setAnnotationsCount}
              currentSlide={slide}
              handleNextSlide={handleNextSlide}
              handlePreviousSlide={handlePreviousSlide}
              isMultipleSelected={isMultipleSelected}
            />
          </Container>
          <ShortcutsPrompt />
        </div>
      </MiniDrawerWithContextMemo>
      <ResizeableThumbnail
        thumbnail={slide?.large_macro_url}
        isVisible={preferences[THUMBNAIL_SWITCH_FIELD]}
      />
    </>
  );
};
